import { isSchemaObject, type SchemaObject } from 'openapi3-ts';

import { addWordBreaks } from '../../utilities';
import styles from './OneOfOptions.module.scss';
import {
  formatSchemaValue,
  getAlternativeKeywords,
  getAlternativeOptions,
  getDivergentPropertyNames,
  getOptionProperties,
  getSelectorFieldName,
  getSelectorValue,
  getSharedPropertyNames,
  getSingleSchemaValue,
  hasAlternatives,
  normalizeSchemaForRendering,
  type SchemaOrReference,
} from './schemaUtils';

type DivergentField = {
  fieldName: string;
  // What defines it; with none, `label` says how it reads instead.
  fields: string[];
  label?: string;
};

type OneOfBranch = {
  selector?: {
    fieldName: string;
    value: string;
  };
  fieldNames: string[];
  fieldNamesAreRequired: boolean;
  divergentFields: DivergentField[];
  label?: string;
  // Nothing distinguishes it, so its label is a position, not a description.
  isUnidentified?: boolean;
  // Needed only when siblings are a different kind of thing, so a list of
  // field names is not set beside a scalar as though they were alike.
  typeLabel?: string;
};

/**
 * Describes a schema by what a reader can see: its values, or its type. A
 * `title` or reference name points at something the reference never renders.
 */
const getSchemaLabel = (
  schema: SchemaOrReference | undefined,
  fallback: string,
): string => {
  if (schema == null || !isSchemaObject(schema)) {
    return fallback;
  }

  const singleValue = getSingleSchemaValue(schema);
  if (singleValue != null) {
    return singleValue;
  }
  if (schema.enum?.length) {
    return schema.enum.map(formatSchemaValue).join(', ');
  }
  if (schema.type === 'array') {
    return schema.items == null
      ? 'array'
      : `[${getSchemaLabel(schema.items, 'object')}]`;
  }
  if (schema.type != null) {
    // OpenAPI 3.1 allows a list of types, e.g. `type: [string, 'null']`.
    const type = Array.isArray(schema.type)
      ? schema.type.join(' or ')
      : schema.type;
    return schema.format == null ? type : `${type} (${schema.format})`;
  }

  return fallback;
};

const getOneOfBranches = (schema?: SchemaObject): OneOfBranch[] => {
  const options = getAlternativeOptions(schema);
  const sharedPropertyNames = new Set(getSharedPropertyNames(options));
  const selectorFieldName = getSelectorFieldName(schema);
  // A field that keeps its own alternatives explains them on its own row, so
  // only a field merged into one row needs accounting for here.
  const divergentPropertyNames = new Set(
    getDivergentPropertyNames(schema).filter(
      (propertyName) => !hasAlternatives(schema?.properties?.[propertyName]),
    ),
  );

  return options.map((option, index) => {
    const properties = getOptionProperties(option);
    const propertyNames = Object.keys(properties);
    const selectorValue = getSelectorValue(option, selectorFieldName);
    const selector =
      selectorFieldName != null && selectorValue != null
        ? { selector: { fieldName: selectorFieldName, value: selectorValue } }
        : {};

    // With no fields of its own — a scalar, an enum — only its values or type
    // describe it.
    if (propertyNames.length === 0) {
      return {
        ...selector,
        fieldNames: [],
        fieldNamesAreRequired: false,
        divergentFields: [],
        label: getSchemaLabel(option, `Option ${index + 1}`),
      };
    }

    const divergentFields = propertyNames
      .filter((propertyName) => divergentPropertyNames.has(propertyName))
      .map((propertyName) => {
        const declaration = normalizeSchemaForRendering(
          properties[propertyName],
        );
        const fields = Object.keys(declaration.properties || {});
        return {
          fieldName: propertyName,
          fields,
          ...(fields.length === 0
            ? { label: getSchemaLabel(declaration, 'a different schema') }
            : {}),
        };
      });
    // Fields unique to this branch: the ones every branch shares are ordinary
    // rows, and the ones they define differently are called out separately.
    const fieldNames = propertyNames.filter(
      (propertyName) =>
        propertyName !== selectorFieldName &&
        !sharedPropertyNames.has(propertyName) &&
        !divergentPropertyNames.has(propertyName),
    );
    const isUnidentified =
      fieldNames.length === 0 && divergentFields.length === 0;

    return {
      ...selector,
      fieldNames,
      fieldNamesAreRequired:
        fieldNames.length > 0 &&
        fieldNames.every((fieldName) =>
          isSchemaObject(option) ? option.required?.includes(fieldName) : false,
        ),
      divergentFields,
      typeLabel: getSchemaLabel(option, 'object'),
      ...(isUnidentified && selectorValue == null
        ? { label: `Option ${index + 1}`, isUnidentified: true }
        : {}),
    };
  });
};

/**
 * Fields only some alternatives accept. The table renders these after the
 * guidance so it reads as a legend for them.
 */
const getOneOfAlternativeFieldNames = (schema?: SchemaObject): string[] => {
  const options = getAlternativeOptions(schema);
  const sharedPropertyNames = new Set(getSharedPropertyNames(options));

  return Array.from(
    new Set(
      options
        .flatMap((option) => Object.keys(getOptionProperties(option)))
        .filter((propertyName) => !sharedPropertyNames.has(propertyName)),
    ),
  );
};

const FieldNames = ({ fieldNames }: { fieldNames: string[] }) => (
  <>
    {fieldNames.map((fieldName, fieldIndex) => (
      <span key={fieldName}>
        {fieldIndex === 0 ? '' : ', '}
        <code className={styles.fieldName}>
          <>{addWordBreaks(fieldName)}</>
        </code>
      </span>
    ))}
  </>
);

const DivergentFields = ({ fields }: { fields: DivergentField[] }) => (
  <>
    {fields.map((field, fieldIndex) => (
      <span key={field.fieldName}>
        {fieldIndex === 0 ? '' : ', '}
        <code className={styles.fieldName}>
          <>{addWordBreaks(field.fieldName)}</>
        </code>{' '}
        {field.fields.length > 0 ? (
          <>
            contains <FieldNames fieldNames={field.fields} />
          </>
        ) : (
          <>
            is <code className={styles.fieldName}>{field.label}</code>
          </>
        )}
      </span>
    ))}
  </>
);

const Branch = ({
  branch,
  qualifyKind,
}: {
  branch: OneOfBranch;
  qualifyKind?: boolean;
}) => {
  const name = branch.label && (
    <code className={styles.fieldName}>{branch.label}</code>
  );
  const selectorClause = branch.selector && (
    <>
      {name ? 'when ' : 'When '}
      <code className={styles.fieldName}>
        <>{addWordBreaks(branch.selector.fieldName)}</>
      </code>{' '}
      is <code>{branch.selector.value}</code>
    </>
  );
  const identifier = (name || selectorClause) && (
    <>
      {name}
      {name && selectorClause ? ' ' : ''}
      {selectorClause}
    </>
  );

  const afterIdentifier = identifier ? ': ' : '';
  const fieldNamesPrefix =
    branch.selector == null
      ? afterIdentifier
      : branch.fieldNamesAreRequired
      ? ', include '
      : '; fields in this schema: ';
  const divergentFieldsPrefix =
    branch.fieldNames.length > 0
      ? '; '
      : branch.selector != null
      ? ', '
      : afterIdentifier;
  return (
    <li>
      {identifier}
      {/* Beside a plain type, a bare list of field names reads as one too. */}
      {qualifyKind && branch.fieldNames.length > 0 && (
        <>
          <code className={styles.fieldName}>{branch.typeLabel}</code>{' '}
          containing{' '}
        </>
      )}
      {branch.fieldNames.length > 0 && (
        <>
          {qualifyKind ? '' : fieldNamesPrefix}
          <FieldNames fieldNames={branch.fieldNames} />
        </>
      )}
      {branch.divergentFields.length > 0 && (
        <>
          {divergentFieldsPrefix}
          <DivergentFields fields={branch.divergentFields} />
        </>
      )}
      .
    </li>
  );
};

/**
 * Explains a schema's alternatives: which values select which, which fields
 * belong together, and how a shared field's definition changes between them.
 */
const OneOfOptions = ({ schema }: { schema?: SchemaObject }) => {
  const branches = getOneOfBranches(schema);

  if (branches.length === 0) {
    return null;
  }

  // Left with no field to account for, an alternative says only which value
  // selects it — already on that field's row — or nothing but its position.
  const saysNothingTheRowsDoNot = branches.every(
    (branch) =>
      branch.fieldNames.length === 0 &&
      branch.divergentFields.length === 0 &&
      (branch.selector != null || branch.isUnidentified),
  );
  if (saysNothingTheRowsDoNot) {
    return null;
  }

  const keywords = getAlternativeKeywords(schema);
  const isExclusive = keywords.length === 1 && keywords[0] === 'oneOf';
  const heading = isExclusive
    ? 'Choose one schema:'
    : 'Choose one or more schemas:';

  const hasSingleRequiredFieldPerBranch =
    isExclusive &&
    branches.every(
      (branch) =>
        branch.selector == null &&
        branch.divergentFields.length === 0 &&
        branch.fieldNames.length === 1 &&
        branch.fieldNamesAreRequired,
    );
  // Alternatives of mixed kinds each need to say which they are, on their own
  // line.
  const isMixedKinds =
    branches.some((branch) => branch.label != null) &&
    branches.some((branch) => branch.fieldNames.length > 0);
  // A branch that carries more than a bare name needs its own line to stay
  // readable; the rest fit on one line as a list of names.
  const needsOwnLine =
    isMixedKinds ||
    branches.some(
      (branch) =>
        branch.selector != null ||
        branch.fieldNames.length > 1 ||
        branch.divergentFields.length > 0,
    );

  if (needsOwnLine) {
    return (
      <div className={styles.container}>
        <strong className={styles.label}>{heading}</strong>
        <ul className={styles.variants}>
          {branches.map((branch, branchIndex) => (
            <Branch
              branch={branch}
              qualifyKind={isMixedKinds}
              key={branchIndex}
            />
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <strong className={styles.label}>
        {hasSingleRequiredFieldPerBranch
          ? 'Exactly one of these fields is required:'
          : heading}
      </strong>{' '}
      <span className={styles.options}>
        {branches.map((branch, branchIndex) => {
          return (
            <span key={branchIndex}>
              {branchIndex === 0 ? '' : ', '}
              <FieldNames fieldNames={branch.fieldNames} />
              {branch.label && (
                <code className={styles.fieldName}>{branch.label}</code>
              )}
            </span>
          );
        })}
      </span>
    </div>
  );
};

export { getOneOfAlternativeFieldNames };
export default OneOfOptions;
